import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpContextToken,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthService } from '@app/core/auth/auth';
import { RefreshTokenResponse } from '@app/core/auth/auth.model';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner';
import { environment } from '@environments/environment';
import { SessionStorageService } from '@app/core/auth/session-storage';
import { WindowRef } from '@app/core/providers/window-ref';

//interceptの処理をスキップするフラグ
export const BYPASS_INTERCEPT = new HttpContextToken(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  //アクセストークンの再取得中かどうかを表すフラグ
  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<RefreshTokenResponse | null> =
    new BehaviorSubject<RefreshTokenResponse | null>(null);
  private overlayRef;

  constructor(
    public authService: AuthService,
    public overlay: Overlay,
    public sessionStorageService: SessionStorageService,
    private readonly windowRef: WindowRef,
  ) {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
    });
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    //BYPASS_INTERCEPTキーがtrueのリクエストはインターセプトの処理をスキップ。
    if (request.context.get(BYPASS_INTERCEPT)) {
      return next.handle(request);
    }

    const accessToken = this.sessionStorageService.getAccessToken() ?? '';
    const idToken = this.sessionStorageService.getIdToken() ?? '';
    const authRequest = this.addTokenHeader(request, accessToken, idToken);

    return next.handle(authRequest).pipe(
      catchError((err: HttpErrorResponse) => {
        //エラーステータスが401（認証エラー）の場合は401アクセストークンを再取得
        if (err.status === 401) {
          return this.handle401Error(authRequest, next);
        }
        return throwError(() => err); //エラーステータスが401以外の場合はエラーを投げるだけ。
      }),
    );
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.tokenRefresh().pipe(
        catchError((err: HttpErrorResponse) => {
          this.isRefreshing = false;
          //0以外の場合はリダイレクト
          if (err.status !== 0) {
            //this.overlayRef.attach(new ComponentPortal(ProgressSpinnerComponent));
            this.overlayRef.attach(new ComponentPortal(SpinnerComponent));
            this.redirect();
          }
          return throwError(() => err);
        }),
        switchMap((newToken: RefreshTokenResponse) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken); //待機中のリクエストに新しい、トークン情報を与える
          return next
            .handle(
              this.addTokenHeader(
                request,
                newToken.access_token,
                newToken.id_token,
              ),
            )
            .pipe(
              catchError((err: HttpErrorResponse) => {
                this.isRefreshing = false;
                //再リクエストが401の場合はリダイレクト
                if (err.status == 401) {
                  this.overlayRef.attach(new ComponentPortal(SpinnerComponent));
                  this.redirect();
                }
                return throwError(() => err); //エラーを投げる
              }),
            );
        }),
      );
    }
    return this.refreshTokenSubject.pipe(
      filter((newToken) => newToken !== null), //refreshTokenがnull以外でセットされた場合take以下を実行
      take(1), //1回目のセットの時だけ、リクエストを送る。

      switchMap((newToken) => {
        return next.handle(
          this.addTokenHeader(
            request,
            newToken.access_token,
            newToken.id_token,
          ),
        );
      }),
    );
  }

  private addTokenHeader(
    request: HttpRequest<unknown>,
    accessToken: string,
    idToken: string,
  ) {
    return request.clone({
      //ヘッダーの情報を再取得したトークン情報に更新して再びリクエストを送る
      headers: request.headers
        .set('x-access-token', accessToken)
        .set('x-id-token', idToken),
    });
  }

  private redirect() {
    this.sessionStorageService.logout();
    this.windowRef.href = `${environment.cgwAuthWebUrl}?applicationCode=${environment.applicationName}#redirectUrl=${this.windowRef.href}`;
  }
}
